/** "Confirm your email" nag.
 *
 * Deliberately soft. Verification gates this strip and nothing else — a
 * confirmation mail that bounces, lands in spam or is simply ignored must never
 * be able to lock someone out of reminders they already created.
 *
 * Dismissal is remembered per account for the session only. Forgetting it on
 * the next visit is the point: it is the whole reminder mechanism.
 *
 * The one thing it is *not* soft about is whether the mail actually went. It
 * used to answer every resend with "Confirmation email sent" — including when
 * no relay was configured and the message had only been written to a log — so a
 * user who never received anything was told, repeatedly, that it had been sent
 * (APP-11). `sent: false` now gets its own copy, and a failed send says what to
 * do next instead of inviting an identical retry.
 */

import { useEffect, useState } from 'react'
import { ApiError, api } from '../lib/api'
import { useStore } from '../lib/store'
import './VerifyBanner.css'

type Status = 'idle' | 'sending' | 'sent' | 'failed'

/** Fallback when the server doesn't name a cooldown. Matches the API default. */
const DEFAULT_COOLDOWN_S = 60

export function VerifyBanner() {
  const user = useStore((s) => s.user)
  const dismissKey = user ? `nudge.verifyDismissed.${user.id}` : ''
  const [dismissed, setDismissed] = useState(
    () => !!dismissKey && sessionStorage.getItem(dismissKey) === '1',
  )
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')
  const [cooldown, setCooldown] = useState(0)

  // Tick the cooldown down so the button re-enables on its own. Hooks must run
  // unconditionally, so this sits above the early return below.
  useEffect(() => {
    if (cooldown <= 0) return
    const t = window.setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => window.clearTimeout(t)
  }, [cooldown])

  if (!user || user.email_verified || dismissed) return null

  async function resend() {
    setStatus('sending')
    try {
      const result = await api.resendVerification()
      setCooldown(result.retry_after_seconds ?? DEFAULT_COOLDOWN_S)
      // `sent: false` from this endpoint means it genuinely did not leave —
      // the server awaits the relay before replying. Treating it as success
      // is what sent people hunting through spam for nothing.
      setStatus(result.sent ? 'sent' : 'failed')
      setMessage(result.detail)
    } catch (e) {
      setStatus('failed')
      if (e instanceof ApiError) {
        // A 429 carries copy worth showing verbatim ("try again in a minute")
        // and, now, the exact wait — so the button can honour it.
        setCooldown(e.retryAfter ?? DEFAULT_COOLDOWN_S)
        setMessage(e.message)
      } else {
        setMessage(
          e instanceof Error
            ? `${e.message} — check your connection and try again.`
            : String(e),
        )
      }
    }
  }

  function dismiss() {
    if (dismissKey) sessionStorage.setItem(dismissKey, '1')
    setDismissed(true)
  }

  const idle = status === 'idle' || status === 'sending'
  // After a successful send the next step is the thing people actually get
  // wrong, so name the folders rather than leaving them to guess.
  const nextStep =
    status === 'sent'
      ? 'It can take a minute. If it has not arrived, look in Spam and Promotions.'
      : null

  return (
    <div className="vb glass" role="status">
      <div className="vb__body">
        <strong>Confirm your email</strong>
        <p>
          {idle
            ? `We sent a link to ${user.email}. It unlocks household invites and account recovery.`
            : message}
        </p>
        {nextStep && <p className="vb__next">{nextStep}</p>}
      </div>
      <div className="vb__actions">
        {/*
          The button stays available after a failure — that is the whole point of
          a retry — but goes quiet for as long as the server will refuse it, so
          an impatient second tap can't come back as another error message.
        */}
        {status !== 'sent' && (
          <button
            className="vb__resend"
            disabled={status === 'sending' || cooldown > 0}
            onClick={resend}
          >
            {status === 'sending'
              ? 'Sending…'
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : status === 'failed'
                  ? 'Try again'
                  : 'Resend'}
          </button>
        )}
        <button className="vb__close" onClick={dismiss} aria-label="Dismiss">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
    </div>
  )
}
